import React, { useContext, useState } from 'react';

import {
    CircularProgress,
    Button,
    Grid,
    Skeleton,
    Table,
    TableRow,
    TableCell,
    TableBody,
    Select,
    MenuItem
} from '@mui/material';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useContainer } from 'unstated-next';

import { RUN_AI_MODEL_FOR_CLASS_SESSION } from '../../../../../config/apiPaths';
import { processApiPath } from '../../../../../utils/url';
import AdminAuthContainer from '../../../AdminAuthContainer';
import { ClassSessionContext } from '../../../ClassSessionContext';

const Spacer = styled.div`
    margin: 1rem;
`;

const PaddedSkeleton = styled(Skeleton)`
    margin: 0.5rem 0;
`;

const StyledSpinner = styled(CircularProgress)`
    color: ${(props) => props.theme.colors.white};
    margin: 3px 8px;
`;

export const ClassSessionDetailAI = () => {
    const search = new URLSearchParams(window.location.search);
    const userUuid = search.get('user_uuid') || window.localStorage.getItem('user_uuid');
    const { t } = useTranslation('portal');
    const { classSessionData } = useContext(ClassSessionContext);
    const { authMode, showDemoContent } = useContainer(AdminAuthContainer);

    const [aiProcess, setAiProcess] = useState(window.localStorage.getItem('aiProcess') || 'troubleshooting');
    const [aiModel, setAiModel] = useState(window.localStorage.getItem('aiModel') || 'gpt-4o');
    const [aiResults, setAiResults] = useState({ loading: true, items: [] });
    const [isLoading, setIsLoading] = useState<boolean>(false);

    console.log('ClassSessionDetailAI classSessionData', classSessionData, 'authMode', authMode);

    const handleAiProcess = (val: string) => {
        setAiProcess(val);
    };
    const handleAiProcessChange = (event) => {
        window.localStorage.setItem('aiProcess', event.target.value);
        handleAiProcess(event.target.value);
    };

    const handleAiModel = (val: string) => {
        setAiModel(val);
    };
    const handleAiModelChange = (event) => {
        window.localStorage.setItem('aiModel', event.target.value);
        handleAiModel(event.target.value);
    };

    const runModel = () => {
        const loadInner = async () => {
            setIsLoading(true);
            const { data } = await axios.post(processApiPath(RUN_AI_MODEL_FOR_CLASS_SESSION), {
                user_uuid: userUuid!,
                auth_mode: authMode,
                demo_content: String(showDemoContent),
                ai_model: aiModel,
                ai_process: aiProcess,
                class_session_uuid: classSessionData.class_session_uuid
            });
            if (data && data.poll) {
                setTimeout(() => {
                    loadInner();
                }, 10000);
                return;
            }
            setAiResults({ loading: false, items: data.data.map((d) => d.data.tool_calls || d.data.responseText) });
            setIsLoading(false);
        };
        loadInner();
    };

    if (!classSessionData || classSessionData.loading) {
        return (
            <>
                <Grid container justifyContent="space-between">
                    <Grid item>
                        <Skeleton width={300} height={60} />
                    </Grid>
                    <Grid item>
                        <Skeleton width={150} height={60} />
                    </Grid>
                </Grid>
                <Spacer />
                <PaddedSkeleton height={35} />
                <PaddedSkeleton height={35} />
                <PaddedSkeleton height={35} />
                <PaddedSkeleton height={35} />
                <PaddedSkeleton height={35} />
                <Spacer />
                <Grid container justifyContent="flex-end">
                    <Skeleton width={400} height={50} />
                </Grid>
            </>
        );
    }

    return (
        <>
            <Table style={{ width: '100%', padding: '0px', margin: '0px' }}>
                <TableBody style={{ width: '100%', padding: '0px', margin: '0px' }}>
                    <TableRow style={{ width: '100%', padding: '0px', margin: '0px' }}>
                        <TableCell>
                            <Select
                                name="aiProcess"
                                onChange={handleAiProcessChange}
                                value={aiProcess}
                                style={{ height: '42px' }}
                            >
                                <MenuItem value="sentiment">{t('ai-process-sentiment')}</MenuItem>
                                <MenuItem value="troubleshooting">{t('ai-process-troubleshooting')}</MenuItem>
                                <MenuItem value="feature_requests">{t('ai-process-featurerequest')}</MenuItem>
                            </Select>
                        </TableCell>
                        <TableCell>
                            <Select
                                name="aiModel"
                                onChange={handleAiModelChange}
                                value={aiModel}
                                style={{ height: '42px' }}
                            >
                                <MenuItem value="meta-llama/Llama-3-70b-chat-hf">{t('llama3')}</MenuItem>
                                <MenuItem value="gpt-3.5-turbo">{t('gpt-3.5')}</MenuItem>
                                <MenuItem value="gpt-4o">{t('gpt-4o')}</MenuItem>
                                <MenuItem value="gpt-4-turbo">{t('gpt-4-turbo')}</MenuItem>
                            </Select>
                        </TableCell>
                        <TableCell>
                            <Button style={{ border: '1px solid #4739e7' }} onClick={runModel}>
                                {t('run-model')}
                            </Button>
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
            <Spacer />
            {!isLoading && aiProcess && (
                <pre
                    style={{
                        fontSize: '12px',
                        overflowX: 'auto',
                        lineHeight: '14px',
                        border: '1px solid rgb(230,230,230)',
                        backgroundColor: 'rgb(240,240,240)',
                        color: 'rgb(0,0,0)',
                        borderRadius: '6px',
                        padding: '20px'
                    }}
                >
                    {JSON.stringify(aiResults.items, null, 2)}
                </pre>
            )}
            {isLoading && aiProcess && (
                <pre
                    style={{
                        fontSize: '12px',
                        overflowX: 'auto',
                        lineHeight: '14px',
                        border: '1px solid rgb(230,230,230)',
                        backgroundColor: 'rgb(240,240,240)',
                        color: 'rgb(0,0,0)',
                        borderRadius: '6px',
                        padding: '20px'
                    }}
                >
                    <StyledSpinner style={{ color: '#4739e7' }}></StyledSpinner>
                    <br />
                    {t('loading-please-be-patient')}
                </pre>
            )}
        </>
    );
};
